// React & Core Libraries
import React, { Fragment, useEffect, useReducer } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Actions
import SoActions from '@Actions/sales/soActions';
import AttachmentActions from '@Actions/attachmentActions';
import AnalyticsActions from '@Actions/application/analyticsActions';
import ActivityLogActions from '@Actions/activityLogActions';

// Helpers & Constants
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import documentUINamesConstant from '@Apis/documentUINamesConstant';

// Components
import PSLines from './psLines';
import InvoiceForm from '../../Invoice/InvoiceForm';
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import ViewDocRightSection from '../../../Common/ViewDocRightSection';
import ViewPackingSlipHeader from './ViewPackingSlipHeader';
import ViewPackingSlipDetails from './ViewPackingSlipDetails';
import ViewPackingSlipFooter from './ViewPackingSlipFooter';
import PRZDrawer from '../../../Common/UI/PRZDrawer';

// Reducer & Utilities
import { initialState, packingSlipViewReducer } from './reducer.js';
import { HideAdhocApprovals, getLinkedDocumentInfo } from './helper';

// Styles & Assets
import 'react-quill/dist/quill.bubble.css';
import './style.scss';

function ViewPackingSlip({
  updateSOStatusLoading,
  updateSOWorkflowStepLoading,
  user,
  updateSOWorkflowStep,
  history,
  getSOByIdLoading,
  getSOById,
  selectedSO,
  updateSOStatus,
  deleteSOLoading,
  deleteSO,
  isQuickView,
  getAttachmentByIdLoading,
  updateAttachmentLoading,
  cancelShopifyOrderLoading,
  MONEY,
  downloadDocument,
  quickUpdateSO,
  priceMasking,
  getSOByIdSuccess,
  selectedTenantId,
  selectedSoId,
  match,
  selectedAttachment,
  updateAttachment,
  getAttachmentById,
}) {
  const [state, dispatch] = useReducer(packingSlipViewReducer, initialState);

  const { isUserReady, isUserReadyForPrefillData, currentUpdate, isGetAttachment, visibleColumns, cfSalesOrderLine, salesOrderType, showNewInvoiceForm, cancellationReason, currentTenantId, currentOrderId, fileList } = state;

  useEffect(() => {
    dispatch({
      type: 'SET_INITIAL_STATE',
      payload: {
        documentUINames: documentUINamesConstant(user),
        salesOrderType: 'PACKING_SLIP',
      },
    });
    return () => {
      getSOByIdSuccess(null);
    };
  }, []);

  useEffect(() => {
    if (user?.tenant_info?.tenant_id && !state.isUserReady) {
      const { orderId } = match.params;
      const tenantId = isQuickView
        ? selectedTenantId
        : Helpers.getTenantEntityPermission(
          user?.user_tenants,
          window.location.href.includes('/estimate')
            ? Helpers.permissionEntities.SALES_ESTIMATE
            : Helpers.permissionEntities.SALES_ORDER,
          Helpers.permissionTypes.READ,
        ).join(',') || user?.tenant_info?.tenant_id;

      const payload = {
        orgId: user?.tenant_info?.org_id,
        tenantId: isQuickView
          ? selectedTenantId
          : Helpers.getTenantEntityPermission(
            user?.user_tenants,
            window.location.href.includes('/estimate')
              ? Helpers.permissionEntities.SALES_ESTIMATE
              : Helpers.permissionEntities.SALES_ORDER,
            Helpers.permissionTypes.READ,
          ).join(',') || user?.tenant_info?.tenant_id,
        soId: isQuickView ? selectedSoId : orderId,
        orderType: salesOrderType,
      };
      getSOById(payload);

      dispatch({
        type: 'SET_USER_READY_ORDERID_TENANTID',
        payload: {
          orderId: isQuickView ? selectedSoId : orderId,
          tenantId,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (selectedSO && !isUserReadyForPrefillData && isUserReady) {
      const lineCf = selectedSO?.sales_order_lines?.[0]?.so_line_custom_fields;
      const soLines = selectedSO.sales_order_lines;
      const soMapping = {};
      for (let i = 0; i < soLines?.length; i++) {
        if (
          soMapping.hasOwnProperty(soLines[i]?.product_sku_info?.product_sku_id)
        ) {
          soMapping[soLines[i]?.product_sku_info?.product_sku_id].push({
            ...soLines[i],
          });
        } else {
          soMapping[soLines[i]?.product_sku_info?.product_sku_id] = [
            {
              ...soLines[i],
            },
          ];
        }
      }

      dispatch({
        type: 'SET_PREFILL_DATA',
        payload: {
          attachments: selectedSO?.attachments,
          accountManagerId: selectedSO?.account_manager_id,
          tags: selectedSO?.tags?.length > 0 ? selectedSO?.tags : [],
          customFields: lineCf,
          visibleColumns: CustomFieldHelpers.updateVisibleColumns(
            lineCf,
            state.visibleColumns,
          ),
          mapping: soMapping,
        },
      });
    }
  }, [selectedSO, isUserReady]);

  useEffect(() => {
    if (
      selectedAttachment &&
      isGetAttachment &&
      !getAttachmentByIdLoading &&
      !updateAttachmentLoading
    ) {
      dispatch({
        type: 'SET_ATTACHMENT_IS_GET_ATTACHMENT',
        payload: {
          fileList: selectedAttachment,
          isGetAttachment: false,
        },
      });
    }
  }, [selectedAttachment]);

  const quickUpdateDisable = !(selectedSO?.status === 'VOID');
  const hasPsEditAccess = Helpers.getPermission(
    Helpers.permissionEntities.PACKING_SLIP,
    Helpers.permissionTypes.UPDATE,
    user,
  );

  const { isDataMaskingPolicyEnable, isHideSellingPrice } = priceMasking;

  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';

  return (
    <Fragment>
      <div className={`view-document__wrapper ${isQuickView ? '' : 'view-document__wrapper-page'}`}>
        {!getSOByIdLoading && !updateSOStatusLoading ? (
          <div className="ant-row">
            <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
              <div className={`view-left__wrapper ${isQuickView ? '' : 'is-page-view'}`}>
                <ViewPackingSlipHeader
                  selectedSO={selectedSO}
                  user={user}
                  isQuickView={isQuickView}
                  updateSOStatus={updateSOStatus}
                  deleteSO={deleteSO}
                  getSOById={getSOById}
                  updateSOStatusLoading={updateSOStatusLoading}
                  deleteSOLoading={deleteSOLoading}
                  history={history}
                  downloadDocument={downloadDocument}
                  cancellationReason={cancellationReason}
                  currentUpdate={currentUpdate}
                  dispatch={dispatch}
                  currentTenantId={currentTenantId}
                  currentOrderId={currentOrderId}
                  salesOrderType={salesOrderType}
                  cancelShopifyOrderLoading={cancelShopifyOrderLoading}
                />
                <ViewPackingSlipDetails
                  selectedSO={selectedSO}
                  user={user}
                  quickUpdateDisable={quickUpdateDisable}
                  hasPsEditAccess={hasPsEditAccess}
                  quickUpdateSO={quickUpdateSO}
                  getSOById={getSOById}
                  currentTenantId={currentTenantId}
                  currentOrderId={currentOrderId}
                  salesOrderType={salesOrderType}
                />
                <PSLines
                  soLines={
                    selectedSO?.sales_order_lines?.map((item) => ({
                      ...item,
                      lineCustomFields: item?.so_line_custom_fields,
                    })) || []
                  }
                  selectedSO={selectedSO}
                  callback={() => {
                    const payload = {
                      orgId: user?.tenant_info?.org_id,
                      tenantId: currentTenantId,
                      soId: currentOrderId,
                      orderType: salesOrderType,
                    };
                    getSOById(payload);
                  }}
                  cfSalesOrderLine={cfSalesOrderLine}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(data) => dispatch({ type: 'SET_VISIBLE_COLUMNS', payload: data })}
                  callbackState={(val) => {
                    dispatch({ type: 'SET_IS_USER_READY_FOR_PREFILL_DATA', payload: val });
                  }}
                  isEditable={quickUpdateDisable && hasPsEditAccess}
                  isSalesOrderPs={!!selectedSO?.packing_slip_id}
                />
                <ViewPackingSlipFooter
                  selectedSO={selectedSO}
                  fileList={fileList}
                  getAttachmentByIdLoading={getAttachmentByIdLoading}
                  updateAttachmentLoading={updateAttachmentLoading}
                  user={user}
                  updateAttachment={updateAttachment}
                  getAttachmentById={getAttachmentById}
                  dispatch={dispatch}
                />
              </div>
            </div>
            <ViewDocRightSection
              entityType='PACKING_SLIP'
              entityId={selectedSO?.order_id}
              MONEY={MONEY}
              user={user}
              isQuickView={isQuickView}
              tagSelectorProps={{
                isEnabled: false,
              }}
              workflowTimeLineProps={{
                isEnabled: true,
                workflowSteps: selectedSO?.workflow_steps || {},
                workflowStepId: selectedSO?.workflow_step_id,
                loading: updateSOWorkflowStepLoading,
                updateStep: updateSOWorkflowStep,
                entityId: currentOrderId,
                HideCase: HideAdhocApprovals(selectedSO),
                entityType: "PACKING_SLIP",
                callback: () => {
                  const payload = {
                    orgId: user?.tenant_info?.org_id,
                    tenantId: currentTenantId,
                    soId: currentOrderId,
                    orderType: salesOrderType,
                  };
                  getSOById(payload);
                },
              }}
              activityLogProps={{
                isEnabled: true,
                entityName: 'Packing Slip',
                entityType: "packing_slip"
              }}
              linkedDocumentsInfo={getLinkedDocumentInfo({ selectedSO, user, isDataMaskingPolicyEnable, isHideSellingPrice, })}
            />
          </div>
        ) : (
          <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
        )}
        <PRZDrawer
          onClose={() => dispatch({ type: 'SET_SHOW_NEW_INVOICE_FORM', payload: false })}
          open={showNewInvoiceForm}
          width="1250px"
          headerWidth="1200px"
          title="Create Invoice"
        >
          <InvoiceForm
            selectedOrderForInvoice={{
              ...(selectedSO?.ps_linked_sales_orders?.[0]
                ? {
                  ...selectedSO.ps_linked_sales_orders[0],
                  sales_order_lines: selectedSO.ps_linked_sales_orders[0]?.sales_order_lines?.map((soLine) => {
                    const availableBatchesTenantDepartmentId = new Set(soLine?.available_batches?.map((batch) => batch?.tenant_department_id));
                    return {
                      ...soLine,
                      available_batches: soLine?.all_product_batches?.filter((batch) => availableBatchesTenantDepartmentId.has(batch?.tenant_department_id)),
                    };
                  }),
                  order_id: selectedSO?.order_id,
                }
                : {
                  ...selectedSO,
                  vehicle_number: null,
                  sales_order_lines: selectedSO?.sales_order_lines?.map(
                    (sol) => ({
                      ...sol,
                      available_batches: sol.available_batches?.filter(
                        (batch) => sol?.product_batches?.some(
                          (productBatch) => productBatch?.batch_id === batch?.batch_id,
                        ),
                      ),
                    }),
                  ),
                }),
            }}
            callback={() => {
              dispatch({ type: 'SET_SHOW_NEW_INVOICE_FORM', payload: false });
              const payload = {
                orgId: user?.tenant_info?.org_id,
                tenantId: currentTenantId,
                soId: currentOrderId,
                orderType: salesOrderType,
              };
              getSOById(payload);
            }}
            isPackingSlipFromSalesOrder={
              !!selectedSO?.ps_linked_sales_orders?.length
            }
            isPackingSlip
            isCarryForward
          />
        </PRZDrawer>
      </div>
    </Fragment>
  );

}

const mapStateToProps = ({ UserReducers, SOReducers, AttachmentReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getSOByIdLoading: SOReducers.getSOByIdLoading,
  selectedSO: SOReducers.selectedSO,
  updateSOStatusLoading: SOReducers.updateSOStatusLoading,
  updateSOWorkflowStepLoading: SOReducers.updateSOWorkflowStepLoading,
  deleteSOLoading: SOReducers.deleteSOLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  updateSOStatus: (payload, callback) => dispatch(SoActions.updateSOStatus(payload, callback)),
  updateSOWorkflowStep: (payload, callback) => dispatch(SoActions.updateSOWorkflowStep(payload, callback)),
  getSOById: (payload, callback) => dispatch(SoActions.getSOById(payload, callback)),
  getSOByIdSuccess: (selectedSO) => dispatch(SoActions.getSOByIdSuccess(selectedSO)),
  deleteSO: (payload, callback) => dispatch(SoActions.deleteSO(payload, callback)),
  getAttachmentById: (entityId, entityName, callback) => dispatch(
    AttachmentActions.getAttachmentById(entityId, entityName, callback),
  ),
  updateAttachment: (payload, callback) => dispatch(AttachmentActions.updateAttachment(payload, callback)),
  downloadDocument: (payload, document) => dispatch(AnalyticsActions.downloadDocument(payload, document)),
  quickUpdateSO: (payload, callback, rollback) => dispatch(SoActions.quickUpdateSO(payload, callback, rollback)),
  getActivityLog: (entityId, entityName, page, limit, callback) => dispatch(
    ActivityLogActions.getActivityLog(
      entityId,
      entityName,
      page,
      limit,
      callback,
    ),
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ViewPackingSlip));
